import React from 'react';
import { Row, Col, Button, Typography, Progress, Input, message } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    ShareAltOutlined,
    EyeOutlined,
    SettingOutlined,
    CopyOutlined,
    PlusOutlined,
    LinkOutlined,
    RocketOutlined,
} from '@ant-design/icons';
import { Utils } from '../../utils/utils';

const { Title, Text } = Typography;

interface Survey {
    id?: number;
    hash: string;
    name?: string;
    status?: string;
    cohortId?: number;
    cohort?: { id: number; name?: string };
}

interface DashboardEmptyStateProps {
    brandName: string;
    totalResponses: number;
    minimumResponses: number;
    hasActiveSurveys: boolean;
    surveys: Survey[];
    onNavigateToSurveys: () => void;
    onNavigateToCreateSurvey: () => void;
    onNavigateToDataSelection?: () => void;
    periodLabel?: string;
}

const DashboardEmptyState: React.FC<DashboardEmptyStateProps> = ({
    brandName,
    totalResponses,
    minimumResponses,
    hasActiveSurveys,
    surveys,
    onNavigateToSurveys,
    onNavigateToCreateSurvey,
    onNavigateToDataSelection,
    periodLabel,
}) => {
    const intl = useIntl();
    const activeSurveys = surveys.filter(s => s.status === 'active');
    const firstActiveSurvey = activeSurveys[0];
    const progressPercent = Math.min(Math.round((totalResponses / minimumResponses) * 100), 100);

    const getContextMessage = () => {
        if (totalResponses === 0 && !hasActiveSurveys) {
            return intl.formatMessage({ id: 'dashboard.emptyState.noSurveys' });
        }
        if (totalResponses === 0 && hasActiveSurveys) {
            return intl.formatMessage({ id: 'dashboard.emptyState.noResponses' });
        }
        return intl.formatMessage(
            { id: 'dashboard.emptyState.progress' },
            { count: totalResponses, min: minimumResponses }
        );
    };

    const handleCopyLink = (surveyHash: string) => {
        const publicUrl = Utils.getPublicSurveyUrl(surveyHash);
        navigator.clipboard.writeText(publicUrl).then(() => {
            message.success(intl.formatMessage({ id: 'dashboard.emptyState.linkCopied' }));
        });
    };

    return (
        <div>
            {/* Hero Section */}
            <div style={{
                background: 'linear-gradient(180deg, #f0f4f8 0%, #ffffff 40%)',
                borderRadius: '20px',
                padding: '40px 32px',
                marginBottom: '24px',
                border: '2px solid #3d5a80',
                boxShadow: '0 10px 40px rgba(61, 90, 128, 0.12), 0 1px 3px rgba(61, 90, 128, 0.08)',
                textAlign: 'center',
            }}>
                <div style={{ marginBottom: 24, textAlign: 'center' }}>
                    <span style={{
                        fontSize: 24,
                        fontWeight: 700,
                        color: '#1b2a41',
                        letterSpacing: '-0.02em',
                    }}>
                        {brandName}
                        <span style={{ color: '#3d5a80', fontWeight: 400, marginLeft: 6 }}>
                            &rsquo;s
                        </span>
                    </span>
                    <span style={{
                        fontSize: 24,
                        fontWeight: 400,
                        color: '#3d5a80',
                        marginLeft: 8,
                        letterSpacing: '-0.01em',
                    }}>
                        Brand Equity Snapshot
                    </span>
                </div>

                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: '20px',
                }}>
                    <Progress
                        type="circle"
                        percent={progressPercent}
                        size={140}
                        strokeColor={{
                            '0%': '#3d5a80',
                            '100%': '#2c4a6e',
                        }}
                        trailColor="rgba(61, 90, 128, 0.15)"
                        format={() => (
                            <div style={{ lineHeight: 1.2 }}>
                                <div style={{ fontSize: '28px', fontWeight: 700, color: '#1b2a41' }}>
                                    {totalResponses}
                                </div>
                                <div style={{ fontSize: '11px', color: '#3d5a80' }}>
                                    of {minimumResponses} responses
                                </div>
                                <div style={{ fontSize: '11px', color: '#3d5a80' }}>
                                    needed
                                </div>
                            </div>
                        )}
                    />
                </div>

                <Text style={{
                    color: '#1b2a41',
                    fontSize: '16px',
                    fontWeight: 500,
                }}>
                    {getContextMessage()}
                </Text>

                {onNavigateToDataSelection && (
                    <div style={{ marginTop: 16 }}>
                        <Text style={{ fontSize: 13, color: '#6b7c93' }}>
                            Showing{' '}
                            <span
                                onClick={onNavigateToDataSelection}
                                title="Change period"
                                style={{
                                    color: '#3d5a80', fontWeight: 500,
                                    cursor: 'pointer', transition: 'all 0.2s ease',
                                    borderBottom: '1px dashed rgba(61,90,128,0.4)',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.color = '#2c4a6e'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.color = '#3d5a80'; }}
                            >
                                {periodLabel || 'Last 30 days'}
                            </span>
                            {' — try a longer period?'}
                        </Text>
                    </div>
                )}
            </div>

            {/* Three Action Cards */}
            <Row gutter={[24, 24]}>
                {/* Card 1: Share Your Surveys */}
                <Col xs={24} md={8}>
                    <div style={{
                        background: '#ffffff',
                        borderRadius: '16px',
                        padding: '24px',
                        height: '100%',
                        border: '1px solid #F59E0B',
                        boxShadow: '0 2px 8px rgba(245, 158, 11, 0.08)',
                        display: 'flex',
                        flexDirection: 'column',
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            marginBottom: '12px',
                        }}>
                            <div style={{
                                width: 36,
                                height: 36,
                                borderRadius: '10px',
                                background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                <ShareAltOutlined style={{ fontSize: 18, color: '#D97706' }} />
                            </div>
                            <Title level={5} style={{ margin: 0, color: '#92400E', fontWeight: 700 }}>
                                <FormattedMessage id="dashboard.emptyState.shareSurveys" />
                            </Title>
                        </div>

                        <Text style={{
                            color: '#78350F',
                            fontSize: '13px',
                            display: 'block',
                            lineHeight: 1.5,
                            minHeight: 40,
                            marginBottom: '16px',
                        }}>
                            <FormattedMessage id="dashboard.emptyState.shareSurveysDesc" />
                        </Text>

                        {activeSurveys.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {activeSurveys.map((survey) => (
                                    <div key={survey.hash} style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        padding: '0 10px',
                                        height: 40,
                                        background: '#FFFBEB',
                                        borderRadius: '8px',
                                        border: '1px solid rgba(245, 158, 11, 0.3)',
                                    }}>
                                        <LinkOutlined style={{ color: '#B45309', fontSize: 13, flexShrink: 0 }} />
                                        <Input
                                            value={Utils.getPublicSurveyUrl(survey.hash)}
                                            readOnly
                                            size="small"
                                            style={{
                                                flex: 1,
                                                fontFamily: 'monospace',
                                                fontSize: '11px',
                                                border: 'none',
                                                background: 'transparent',
                                                color: '#78350F',
                                                padding: '2px 4px',
                                            }}
                                        />
                                        <Button
                                            size="small"
                                            icon={<CopyOutlined />}
                                            onClick={() => handleCopyLink(survey.hash)}
                                            style={{
                                                background: '#F59E0B',
                                                border: 'none',
                                                color: 'white',
                                                flexShrink: 0,
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={onNavigateToCreateSurvey}
                                style={{
                                    background: '#F59E0B',
                                    border: 'none',
                                    fontWeight: 600,
                                    borderRadius: '8px',
                                    width: '100%',
                                }}
                            >
                                <FormattedMessage id="dashboard.emptyState.createSurvey" />
                            </Button>
                        )}
                    </div>
                </Col>

                {/* Card 2: Preview Your Survey */}
                <Col xs={24} md={8}>
                    <div style={{
                        background: '#ffffff',
                        borderRadius: '16px',
                        padding: '24px',
                        height: '100%',
                        border: '1px solid #10B981',
                        boxShadow: '0 2px 8px rgba(16, 185, 129, 0.08)',
                        display: 'flex',
                        flexDirection: 'column',
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            marginBottom: '12px',
                        }}>
                            <div style={{
                                width: 36,
                                height: 36,
                                borderRadius: '10px',
                                background: 'linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                <EyeOutlined style={{ fontSize: 18, color: '#059669' }} />
                            </div>
                            <Title level={5} style={{ margin: 0, color: '#065F46', fontWeight: 700 }}>
                                <FormattedMessage id={activeSurveys.length > 1 ? 'dashboard.emptyState.previewSurveys' : 'dashboard.emptyState.previewSurvey'} />
                            </Title>
                        </div>

                        <Text style={{
                            color: '#047857',
                            fontSize: '13px',
                            display: 'block',
                            lineHeight: 1.5,
                            minHeight: 40,
                            marginBottom: '16px',
                        }}>
                            <FormattedMessage id="dashboard.emptyState.previewSurveyDesc" />
                        </Text>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {activeSurveys.length > 0 ? activeSurveys.map((survey) => (
                                <Button
                                    key={survey.hash}
                                    type="primary"
                                    icon={<RocketOutlined />}
                                    onClick={() => {
                                        const testUrl = Utils.getTestSurveyUrl(survey.hash);
                                        window.open(testUrl, '_blank');
                                    }}
                                    style={{
                                        background: '#10B981',
                                        border: 'none',
                                        fontWeight: 600,
                                        borderRadius: '8px',
                                        width: '100%',
                                        height: 40,
                                    }}
                                >
                                    <FormattedMessage id="dashboard.emptyState.openPreview" />
                                </Button>
                            )) : (
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={onNavigateToCreateSurvey}
                                    style={{
                                        background: '#10B981',
                                        border: 'none',
                                        fontWeight: 600,
                                        borderRadius: '8px',
                                        width: '100%',
                                    }}
                                >
                                    <FormattedMessage id="dashboard.emptyState.createSurvey" />
                                </Button>
                            )}
                        </div>
                    </div>
                </Col>

                {/* Card 3: Manage Surveys */}
                <Col xs={24} md={8}>
                    <div style={{
                        background: '#ffffff',
                        borderRadius: '16px',
                        padding: '24px',
                        height: '100%',
                        border: '1px solid #3B82F6',
                        boxShadow: '0 2px 8px rgba(59, 130, 246, 0.08)',
                        display: 'flex',
                        flexDirection: 'column',
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            marginBottom: '12px',
                        }}>
                            <div style={{
                                width: 36,
                                height: 36,
                                borderRadius: '10px',
                                background: 'linear-gradient(135deg, #DBEAFE 0%, #BFDBFE 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                <SettingOutlined style={{ fontSize: 18, color: '#2563EB' }} />
                            </div>
                            <Title level={5} style={{ margin: 0, color: '#1E40AF', fontWeight: 700 }}>
                                <FormattedMessage id="dashboard.emptyState.manageSurveys" />
                            </Title>
                        </div>

                        <Text style={{
                            color: '#1D4ED8',
                            fontSize: '13px',
                            display: 'block',
                            lineHeight: 1.5,
                            minHeight: 40,
                            marginBottom: '16px',
                        }}>
                            <FormattedMessage id="dashboard.emptyState.manageSurveysDesc" />
                        </Text>

                        <div>
                            <Button
                                type="primary"
                                icon={<SettingOutlined />}
                                onClick={onNavigateToSurveys}
                                style={{
                                    background: '#3B82F6',
                                    border: 'none',
                                    fontWeight: 600,
                                    borderRadius: '8px',
                                    width: '100%',
                                    height: 40,
                                }}
                            >
                                <FormattedMessage id="dashboard.emptyState.goToSurveys" />
                            </Button>
                        </div>
                    </div>
                </Col>
            </Row>
        </div>
    );
};

export default DashboardEmptyState;
